import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell
} from "recharts";
import {
  Layers, Plus, ChevronRight, Clock, CheckCircle, AlertTriangle,
  Users, Shield, Briefcase, User, Play, Eye
} from "lucide-react";

import { API } from "../lib/api";

const PHASE_ICONS = { individual_contributor: User, manager: Briefcase, executive: Shield };
const STATUS_STYLES = {
  active: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", label: "Active" },
  pending: { bg: "bg-gray-50", text: "text-gray-500", border: "border-gray-200", label: "Pending" },
  completed: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Complete" },
};
const GAP_STYLES = {
  aligned: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Aligned" },
  mild_variance: { bg: "bg-amber-50", text: "text-amber-700", label: "Mild Variance" },
  critical_gap: { bg: "bg-red-50", text: "text-red-700", label: "Critical Gap" },
};

function pillarLabel(k) { return k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()); }

function RealityGapChart({ gaps }) {
  if (!gaps || gaps.length === 0) return null;
  const radarData = gaps.map(g => ({
    pillar: pillarLabel(g.pillar),
    "Coworkers (Reality)": g.ic_avg || 0,
    "Managers (Interpretation)": g.manager_avg || 0,
    "Executives (Perception)": g.executive_avg || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={340}>
      <RadarChart data={radarData} margin={{ top: 10, right: 40, bottom: 10, left: 40 }}>
        <PolarGrid stroke="#E5E7EB" />
        <PolarAngleAxis dataKey="pillar" tick={{ fontFamily: "IBM Plex Sans", fontSize: 10, fill: "#4B5563" }} />
        <PolarRadiusAxis angle={30} domain={[0, 5]} tickCount={6} tick={{ fontSize: 9, fill: "#9CA3AF" }} />
        <Radar name="Coworkers (Reality)" dataKey="Coworkers (Reality)" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.15} strokeWidth={2} />
        <Radar name="Managers (Interpretation)" dataKey="Managers (Interpretation)" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.1} strokeWidth={2} />
        <Radar name="Executives (Perception)" dataKey="Executives (Perception)" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.08} strokeWidth={2} />
        <Legend wrapperStyle={{ fontFamily: "IBM Plex Sans", fontSize: 11 }} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

function GapBarChart({ gaps }) {
  if (!gaps || gaps.length === 0) return null;
  const data = gaps.map(g => ({
    pillar: pillarLabel(g.pillar).substring(0, 10),
    "IC-Mgr Gap": g.ic_mgr_gap || 0,
    "Mgr-Exec Gap": g.mgr_exec_gap || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ left: -10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
        <XAxis dataKey="pillar" tick={{ fontSize: 10, fill: "#6B7280" }} />
        <YAxis domain={[0, 3]} tick={{ fontSize: 10, fill: "#9CA3AF" }} />
        <Tooltip contentStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12 }} />
        <Bar dataKey="IC-Mgr Gap" fill="#3B82F6" radius={[4, 4, 0, 0]} />
        <Bar dataKey="Mgr-Exec Gap" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Campaign Detail View ──────────────────────────────────────────────────
function CampaignDetail({ campaignId, onBack }) {
  const [campaign, setCampaign] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/campaigns/${campaignId}`, { withCredentials: true }),
      axios.get(`${API}/api/campaigns/${campaignId}/gaps`, { withCredentials: true }).catch(() => ({ data: { gaps: [], summary: {} } })),
    ]).then(([c, g]) => {
      setCampaign(c.data);
      setGaps(g.data);
    }).finally(() => setLoading(false));
  }, [campaignId]);

  const forceAdvance = async () => {
    await axios.post(`${API}/api/campaigns/${campaignId}/force-advance`, {}, { withCredentials: true });
    const { data } = await axios.get(`${API}/api/campaigns/${campaignId}`, { withCredentials: true });
    setCampaign(data);
    const g = await axios.get(`${API}/api/campaigns/${campaignId}/gaps`, { withCredentials: true }).catch(() => ({ data: { gaps: [], summary: {} } }));
    setGaps(g.data);
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!campaign) return null;

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="font-ibm-plex text-sm text-slate-500 hover:text-slate-900 mb-2 block">
            &larr; Back to campaigns
          </button>
          <h2 className="font-outfit text-2xl font-black text-slate-900">{campaign.name}</h2>
          <p className="font-ibm-plex text-sm text-slate-500">
            Status: <span className="font-semibold capitalize">{campaign.status}</span> · Created {new Date(campaign.created_at).toLocaleDateString()}
          </p>
        </div>
        {campaign.status === "active" && (
          <button data-testid="force-advance-btn" onClick={forceAdvance}
            className="flex items-center gap-2 bg-slate-900 text-white font-outfit font-medium px-4 py-2 rounded-lg hover:bg-slate-800 text-sm">
            <Play className="w-3.5 h-3.5" /> Advance Phase
          </button>
        )}
      </div>

      {/* Phase Progress */}
      <div className="grid grid-cols-3 gap-4">
        {campaign.phases?.map((phase, i) => {
          const Icon = PHASE_ICONS[phase.role] || User;
          const st = STATUS_STYLES[phase.status] || STATUS_STYLES.pending;
          return (
            <div key={i} data-testid={`phase-${phase.role}`}
              className={`bg-white border-2 ${st.border} rounded-xl p-5 transition-all`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${st.bg}`}>
                    <Icon className={`w-4 h-4 ${st.text}`} />
                  </div>
                  <span className="font-outfit text-sm font-bold text-slate-900">{phase.label}</span>
                </div>
                <span className={`text-[10px] font-ibm-plex font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>
                  {st.label}
                </span>
              </div>
              {/* Progress */}
              <div className="mb-2">
                <div className="flex justify-between mb-1">
                  <span className="font-ibm-plex text-xs text-slate-500">{phase.submitted}/{phase.total} responses</span>
                  <span className="font-outfit text-xs font-bold text-slate-700">{phase.completion_pct}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${phase.status === "completed" ? "bg-emerald-500" : "bg-blue-500"}`}
                    style={{ width: `${phase.completion_pct}%` }} />
                </div>
              </div>
              {phase.deadline && (
                <p className="font-ibm-plex text-[10px] text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Deadline: {new Date(phase.deadline).toLocaleString("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Reality Gap Analysis */}
      {gaps && gaps.gaps?.length > 0 && (
        <>
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-purple-600" />
                <h3 className="font-outfit text-base font-bold text-slate-900">Reality vs Perception</h3>
              </div>
              {gaps.summary && (
                <div className="flex gap-2">
                  {gaps.summary.aligned > 0 && <span className="text-[10px] font-ibm-plex font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{gaps.summary.aligned} Aligned</span>}
                  {gaps.summary.mild > 0 && <span className="text-[10px] font-ibm-plex font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{gaps.summary.mild} Mild</span>}
                  {gaps.summary.critical > 0 && <span className="text-[10px] font-ibm-plex font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700">{gaps.summary.critical} Critical</span>}
                </div>
              )}
            </div>
            <RealityGapChart gaps={gaps.gaps} />
          </div>

          {/* Gap Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-outfit text-base font-bold text-slate-900 mb-4">Gap Magnitude</h3>
              <GapBarChart gaps={gaps.gaps} />
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-outfit text-base font-bold text-slate-900 mb-4">Pillar Gap Detail</h3>
              <div className="space-y-2">
                {gaps.gaps.map((g) => {
                  const gapStatus = g.ic_mgr_status || "aligned";
                  const gs = GAP_STYLES[gapStatus] || GAP_STYLES.aligned;
                  return (
                    <div key={g.pillar} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100">
                      <span className="font-ibm-plex text-sm text-slate-700 flex-1">{pillarLabel(g.pillar)}</span>
                      <div className="flex items-center gap-4 text-xs font-ibm-plex">
                        <span className="text-blue-600">IC: {g.ic_avg ?? "—"}</span>
                        <span className="text-amber-600">Mgr: {g.manager_avg ?? "—"}</span>
                        <span className="text-purple-600">Exec: {g.executive_avg ?? "—"}</span>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${gs.bg} ${gs.text}`}>
                        {gs.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {(!gaps || gaps.gaps?.length === 0) && campaign.status !== "completed" && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <Layers className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-outfit text-lg font-bold text-slate-700 mb-1">Gap analysis pending</p>
          <p className="font-ibm-plex text-sm text-slate-500">
            Reality gap data will be available after all survey phases are complete.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Main Campaigns Page ────────────────────────────────────────────────────
export default function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/campaigns`, { withCredentials: true })
      .then(({ data }) => setCampaigns(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api/campaigns`, { name: newName }, { withCredentials: true });
      setCampaigns((prev) => [data, ...prev]);
      setNewName("");
      setSelected(data.id);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create campaign");
    } finally {
      setCreating(false);
    }
  };

  if (selected) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-8">
          <CampaignDetail campaignId={selected} onBack={() => setSelected(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-8 animate-slide-up">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Layers className="w-5 h-5 text-purple-600" />
              <h1 className="font-outfit text-3xl font-black text-slate-900">Intelligence Campaigns</h1>
            </div>
            <p className="font-ibm-plex text-sm text-slate-500">
              Multi-layer assessments with Reality Gap analysis
            </p>
          </div>
        </div>

        {/* Create */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 flex items-center gap-3 animate-slide-up stagger-1">
          <input data-testid="campaign-name-input" type="text" value={newName}
            onChange={(e) => setNewName(e.target.value)} placeholder="Campaign name (e.g. Q1 2026 Assessment)"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900" />
          <button data-testid="create-campaign-btn" onClick={handleCreate} disabled={creating || !newName.trim()}
            className="flex items-center gap-2 bg-slate-900 text-white font-outfit font-medium px-5 py-2.5 rounded-lg hover:bg-slate-800 text-sm disabled:opacity-60">
            <Plus className="w-4 h-4" /> {creating ? "Creating..." : "Launch Campaign"}
          </button>
        </div>

        {/* Campaign List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-16 text-center animate-fade-in">
            <Layers className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <h3 className="font-outfit text-xl font-bold text-slate-900 mb-2">No campaigns yet</h3>
            <p className="font-ibm-plex text-slate-500 text-sm">
              Launch a multi-layer campaign to collect Reality vs Perception data across your organization.
            </p>
          </div>
        ) : (
          <div className="space-y-3 animate-slide-up stagger-2">
            {campaigns.map((c) => (
              <div key={c.id} onClick={() => setSelected(c.id)}
                data-testid={`campaign-${c.id}`}
                className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4 cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  c.status === "completed" ? "bg-emerald-50" : "bg-blue-50"
                }`}>
                  <Layers className={`w-5 h-5 ${c.status === "completed" ? "text-emerald-600" : "text-blue-600"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-outfit font-semibold text-slate-900">{c.name}</h3>
                  <div className="flex items-center gap-3 mt-1">
                    {c.phases?.map((p, i) => {
                      const st = STATUS_STYLES[p.status] || STATUS_STYLES.pending;
                      return (
                        <span key={i} className={`text-[10px] font-ibm-plex font-medium px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>
                          {p.label}: {st.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-ibm-plex text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString()}</p>
                  {c.gaps_calculated && (
                    <span className="text-[10px] font-ibm-plex font-medium text-purple-600">Gaps Ready</span>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
